import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ForgotUsername } from '@shared/models/forgot-username.model';
import { ChangeStatusUserRequest } from '@shared/models/user-information.model';
import { UserDto, UserViewResponse } from '@shared/models/user.model';
import { selectUserInfoResult } from '@shared/store/selectors/auth-user-info.selector';
import { AppStates } from '@shared/store/state/app.state';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';

import { BaseService } from './base.service';

@Injectable({
  providedIn: 'root'
})
export class UsersService extends BaseService {
  public override headers: HttpHeaders;
  private userName: string;

  constructor(
    private readonly http: HttpClient,
    private readonly logger: NGXLogger,
    private readonly store: Store<AppStates>
  ) {
    super();
    this.envService = this.env.services.users;
    this.headers = new HttpHeaders(this.envService.headers);
    this.store.select(selectUserInfoResult).subscribe((val) => {
      if (val) {
        this.userName = val.userName;
      }
    });
  }

  public resetPassword(userName: string): Observable<HttpResponse<any>> {
    const url = this.getFullUrl(this.env.services.users.resetPassword, { userName });
    return this.http.post<any>(url, null, { headers: this.headers, observe: 'response' });
  }

  public resetPinCode(userName: string): Observable<HttpResponse<any>> {
    const url = this.getFullUrl(this.env.services.users.resetPinCode, {
      userName
    });
    return this.http.post<any>(url, null, { headers: this.headers, observe: 'response' });
  }

  public forgotUsername<T>(mobileNumber: ForgotUsername, accessToken: string): Observable<HttpResponse<T>> {
    const headers = this.headers.append('Authorization', `Bearer ${accessToken}`);
    const url = this.getFullUrl(this.env.services.users.forgotUsername, {
      mobileNumber: mobileNumber.mobileNumber
    });
    return this.http.post<T>(url, null, { headers, observe: 'response' });
  }

  public forgotPasswordCreateOTP<T>(userName: string, accessToken: string): Observable<HttpResponse<T>> {
    const headers = this.headers.append('Authorization', `Bearer ${accessToken}`);
    const url = this.getFullNestUrl(this.envService.createOTP, { userName });

    return this.http.post<T>(url, null, { headers, observe: 'response' });
  }

  public forgotPasswordVerifyOTP<T>(userName: string, otp: string, accessToken: string): Observable<HttpResponse<T>> {
    const headers = this.headers.append('Authorization', `Bearer ${accessToken}`);
    const url = this.getFullNestUrl(this.envService.verifyOTP, { userName });
    const data = { otp };
    return this.http.post<T>(url, data, { headers, observe: 'response' });
  }

  public forgotPassword<T>(
    userName: string,
    newPassword: string,
    otp: string,
    accessToken: string
  ): Observable<HttpResponse<T>> {
    const headers = this.headers.append('Authorization', `Bearer ${accessToken}`);
    const url = this.getFullNestUrl(this.envService.forgotPassword, { userName });
    const data = { newPassword, otp };
    return this.http.post<T>(url, data, { headers, observe: 'response' });
  }

  public crateUser(data: UserDto) {
    const url = this.getNestUrl();
    return this.http.post<any>(url, data, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  public updateUser(data: UserDto) {
    const url = this.getNestUrl();

    return this.http.put<any>(url, data, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  public getUserByNo(userNo: string) {
    const url = this.getFullNestUrl(this.envService.get, { no: userNo });
    return this.http.get<UserViewResponse>(url, { headers: this.headers, observe: 'body' });
  }

  public changeStatusUserByUserName(user: ChangeStatusUserRequest) {
    const url = this.getFullNestUrl(this.envService.activateUser, {
      userName: user.userName,
      status: `${user.status}`
    });
    return this.http.put<any>(url, {}, { headers: this.headers, observe: 'response' });
  }
}
